import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useProject } from 'features/project/hooks/use-project'
import { useProjectStore } from 'features/project/store'
import { ProjectDetails, ProjectState } from 'types/project'

export const useProjectDetails = (
  projectId: string,
): {
  details: ProjectDetails | undefined
  isLoading: boolean
  isError: boolean
} => {
  const navigate = useNavigate()
  const location = useLocation()
  const setDetails = useProjectStore((state: ProjectState) => state.setDetails)

  // `retry: false` is localized here (rather than on the underlying useProject)
  // so consumers like ProjectRoot can branch on `isError` quickly instead of
  // sitting on a spinner for 3 retry rounds. See PROD-3476.
  const {
    data: rawDetails,
    isLoading,
    isError,
  } = useProject(projectId, { retry: false })
  const details = rawDetails as ProjectDetails | undefined

  useEffect(() => {
    if (!details) return

    if ((details as ProjectDetails & { redirect_url?: string }).redirect_url) {
      const newPath = location.pathname.replace(
        `/project/${projectId}`,
        (details as ProjectDetails & { redirect_url?: string }).redirect_url!,
      )
      const newUrl = `${newPath}${location.search}${location.hash}`
      navigate(newUrl, { replace: true })
      return
    }

    setDetails(details)
  }, [
    details,
    projectId,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    setDetails,
  ])

  return { details, isLoading, isError }
}
